import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";

import { getPublicCategory } from "@/categories/api/queries";
import { getPublicEvent } from "@/events/api/queries";
import { MWS_PLATFORM_ID } from "@/plugins/storePopulation";
import { getPublicProduct } from "@/products/api/queries";

const kindByPathPrefix = {
  product: "product",
  event: "event",
  category: "category",
  "all-products": "category",
  "search/category": "category",
} as const;

type WebsitePage = {
  kind: (typeof kindByPathPrefix)[keyof typeof kindByPathPrefix];
  identifier: string;
};

export const parseWebsitePath = (path: string): WebsitePage | undefined => {
  const [, prefix, identifier] =
    path.match(
      /^\/(?:[a-z]{2}\/)?(product|event|category|all-products|search\/category)\/([^/]+)\/?$/,
    ) ?? [];

  return prefix && identifier
    ? {
        kind: kindByPathPrefix[prefix as keyof typeof kindByPathPrefix],
        identifier,
      }
    : undefined;
};

export const resolveWebsitePath = async (path: string) => {
  const page = parseWebsitePath(path);
  if (!page) return "/";

  try {
    if (page.kind === "product") {
      const product = await getPublicProduct(page.identifier, MWS_PLATFORM_ID);

      return {
        name: "/product-listings/[productListingId]/details" as const,
        params: { productListingId: product.id },
      };
    }

    if (page.kind === "category") {
      const category = await getPublicCategory(
        page.identifier,
        MWS_PLATFORM_ID,
      );

      return {
        name: "/categories/[categoryId]/details" as const,
        params: { categoryId: category.id },
      };
    }

    const event = await getPublicEvent(page.identifier, MWS_PLATFORM_ID);

    return {
      name: "/events/[eventId]/details" as const,
      params: { eventId: event.id },
    };
  } catch (error) {
    if (!isAxiosError(error) || error.status !== 404) {
      Sentry.captureException(error);
    }

    return "/";
  }
};
